/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

// Azure
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../authConfig";

// Azure
export const msalInstance = new PublicClientApplication(msalConfig);

/* Azure
 * alternative solution:
 * add "proxy": "http://localhost:8080", in the package.json
 * For callApiToLogin() and callApiToLogout()
 * => add const url = process.env.REACT_APP_SERVER_URI;
 * For callApiToGetSpaCode()
 * => add const url = process.env.REACT_APP_URI;
 */

/**
 * Calls a REST API endpoint using a Bearer token for authentication and returns the parsed JSON response.
 *
 * @param {string} accessToken - The Bearer access token for authentication.
 * @param {string} apiEndpoint - The full API endpoint URL.
 * @returns {Promise<Object>} The parsed JSON response from the API.
 * @example
 * const data = await callApiWithToken("your-access-token", "https://api.example.com/data");
 */
export const callApiWithToken = async (accessToken, apiEndpoint) => {
	const headers = new Headers();
	headers.append("Authorization", `Bearer ${accessToken}`);

	const options = {
		method: "GET",
		headers,
	};

	try {
		const response = await fetch(apiEndpoint, options);
		return await response.json();
	} catch (error) {
		console.error("API call failed:", error);
		return null;
	}
};

/**
 * Sends a login request to the `/auth/login` API endpoint and returns the JSON response.
 *
 * @async
 * @returns {Promise<Object|null>} The parsed JSON response from the API, or null if there is an error.
 *
 * @example
 * const result = await callApiToLogin();
 * if (result?.success) { ... }
 */
export const callApiToLogin = async () => {
	// Server already issues a 302 to Azure. Just navigate to the route.
	window.location.assign("/auth/login");
};

/**
 * Logs out the current user by calling the logout API endpoint and removing session information.
 * @returns {Promise<Object|null>} The response from the server as JSON, or null on error.
 * @example
 * const result = await callApiToLogout();
 */
export const callApiToLogout = async () => {
	try {
		if (sessionStorage.hasOwnProperty("desiredPage")) {
			sessionStorage.removeItem("desiredPage");
		}
		const response = await fetch("/auth/logout");
		return await response.json();
	} catch (error) {
		console.error("Logout API call failed:", error);
		return null;
	}
};

/**
 * Fetches a SPA code from the backend authentication endpoint.
 * @returns {Promise<Object|null>} The response from the server as JSON, or null on error.
 * @example
 * const code = await callApiToGetSpaCode();
 */
export const callApiToGetSpaCode = async () => {
	try {
		const response = await fetch("/auth/fetchCode", { method: "GET", credentials: "include" });
		return await response.json();
	} catch (error) {
		console.error("Fetch SPA code API call failed:", error);
		return null;
	}
};
